import crypto from 'crypto-browserify';

export default class cryptDecrypt{ 

    private readonly ALGORITHM = 'aes-256-cbc';
    private readonly KEY: Buffer;
    private readonly IV: Buffer;

    /**
     * Constructor initializes encryption settings
     */
    constructor(secretKey: string) {
        
        this.KEY = crypto.createHash('sha256').update(secretKey).digest(); // Deriva una chiave sicura
        this.IV = Buffer.alloc(16, 0); // IV fisso (non consigliato per massima sicurezza)

    }

    /**
     * Generates a secure random key
     * @returns The generated key
     */
    genRandomKey(): string{
        
        const secureKey = crypto.randomBytes(32).toString("hex");
        return secureKey;

    }

    /**
     * Encrypts an array using AES-256-CBC encryption
     * @param array - Data to be encrypted
     * @returns { encryptedData: string, iv: string } - The encrypted data and IV
     */
    encryptArray(array: unknown[]): { encryptedData: string; iv: string } {
        
        const cipher = crypto.createCipheriv(this.ALGORITHM, this.KEY, this.IV);
        let encrypted = cipher.update(JSON.stringify(array), 'utf8', 'hex');
        encrypted += cipher.final('hex');
        return { encryptedData: encrypted, iv: this.IV.toString('hex') };

    }

    /**
     * Decrypts an encrypted array
     * @param encryptedData - The encrypted data in hex format
     * @param iv - Initialization Vector used for encryption
     * @returns The decrypted array
     */
    decryptArray(encryptedData: string, iv: string): unknown[] {
        
        const decipher = crypto.createDecipheriv(this.ALGORITHM, this.KEY, Buffer.from(iv, 'hex'));
        let decrypted = decipher.update(encryptedData, 'hex', 'utf8');
        decrypted += decipher.final('utf8');
        return JSON.parse(decrypted);

    }

    /**
     * Encrypts an object using AES-256-CBC encryption
     * @param obj - Object to be encrypted
     * @returns { encryptedData: string, iv: string } - The encrypted data and IV
     */
    encryptObject<T extends object>(obj: T): { encryptedData: string; iv: string }{

        const cipher = crypto.createCipheriv(this.ALGORITHM, this.KEY, this.IV);
        let encrypted = cipher.update(JSON.stringify(obj), 'utf8', 'hex');
        encrypted += cipher.final('hex');
        return { encryptedData: encrypted, iv: this.IV.toString('hex') };

    }

    /**
     * Decrypts an encrypted object
     * @param encryptedData - The encrypted object data in hex format
     * @param iv - Initialization Vector used for encryption
     * @returns The decrypted object
     */
    decryptObject<T extends object>(encryptedData: string, iv: string): T{

        const decipher = crypto.createDecipheriv(this.ALGORITHM, this.KEY, Buffer.from(iv, 'hex'));
        let decrypted = decipher.update(encryptedData, 'hex', 'utf8');
        decrypted += decipher.final('utf8');
        return JSON.parse(decrypted) as T;

    }

    /**
     * Async version - Encrypts an array using AES-256-CBC encryption
     * @param array - Data to be encrypted
     * @returns Promise<{ encryptedData: string, iv: string }>
     */
    async encryptArrayAsync(array: unknown[]): Promise<{ encryptedData: string; iv: string }> {
        return new Promise((resolve) => {
            const result = this.encryptArray(array);
            resolve(result);
        });
    }

    /**
     * Async version - Decrypts an encrypted array
     * @param encryptedData - The encrypted data in hex format
     * @param iv - Initialization Vector used for encryption
     * @returns Promise<unknown[]>
     */
    async decryptArrayAsync(encryptedData: string, iv: string): Promise<unknown[]> {
        return new Promise((resolve) => {
            const result = this.decryptArray(encryptedData, iv);
            resolve(result);
        });
    }

    /**
     * Async version - Encrypts an object using AES-256-CBC encryption
     * @param obj - Object to be encrypted
     * @returns Promise<{ encryptedData: string, iv: string }>
     */
    async encryptObjectAsync<T extends object>(obj: T): Promise<{ encryptedData: string; iv: string }> {
        return new Promise((resolve) => {
            const result = this.encryptObject(obj);
            resolve(result);
        });
    }

    /**
     * Async version - Decrypts an encrypted object
     * @param encryptedData - The encrypted object data in hex format
     * @param iv - Initialization Vector used for encryption
     * @returns Promise<T>
     */
    async decryptObjectAsync<T extends object>(encryptedData: string, iv: string): Promise<T> {
        return new Promise((resolve) => {
            const result = this.decryptObject<T>(encryptedData, iv);
            resolve(result);
        });
    }

    /**
     * Test function to demonstrate encryption and decryption
     * @returns An object containing original, encrypted, and decrypted data
     */
    protected test(): { original: unknown[], encrypted: string, decrypted: unknown[] }{

        const array: unknown[] = [1, 2, 3, "ciao", { nome: "Mario" }];
        const encrypted = this.encryptArray(array);
        const decrypted = this.decryptArray(encrypted.encryptedData, encrypted.iv);

        return { original: array, encrypted: encrypted.encryptedData, decrypted };

    }

    /**
     * Test function to demonstrate object encryption and decryption
     * @returns An object containing original, encrypted, and decrypted object data
     */
    protected testObjectEncryption(): { original: object; encrypted: string; decrypted: object }{

        const obj = { id: 1, username: "john_doe", role: "admin" };
        const encrypted = this.encryptObject(obj);
        const decrypted = this.decryptObject<object>(encrypted.encryptedData, encrypted.iv);

        return { original: obj, encrypted: encrypted.encryptedData, decrypted };
        
    }

}